import type { TopUpView } from '@/api/topup';

/**
 * One place that decides what a top-up request SAYS about itself.
 *
 * <p>Before this module the same question was answered by seven independent expressions inside
 * `VitrineTopUpView.vue`, and they had already drifted apart: the list row computed its stage from the
 * request status alone while the detail screen added the on-chain phase, so one request read "Принята"
 * in the list and "Средства получены" on the card, in the same poll tick, for as long as it lived. The
 * headline slot answered a third question ("has anything been credited?") in words that sounded like a
 * fourth ("has the transfer arrived?") — which is how a client who had already sent USDT was shown
 * "Ждём перевод".</p>
 *
 * <p><b>Functions here return KEYS and numbers, never rendered text.</b> `t()` belongs to the component:
 * `useI18n()` outside a setup context throws, and a module that reaches for a store or a ref is not a
 * pure function any more — it is the same tangle one file over.</p>
 */

/** Labels of the four-step fiat timeline. Index order IS the stage order. */
export const STAGES = [
  'vitrine.topup.tl.accepted',
  'vitrine.topup.tl.send',
  'vitrine.topup.tl.received',
  'vitrine.topup.tl.credited',
] as const;

/**
 * Cash walkthrough 24.10, П2: a cash client hands banknotes over at a cash point — there is nothing to «transfer»
 * and no «requisites», only a place, a time and an amount. The bank wording key → its cash counterpart; a key not
 * listed reads the same for both.
 */
const CASH_WORDING: Readonly<Record<string, string>> = {
  'vitrine.topup.payment.toTransfer': 'vitrine.topup.payment.toHandOver',
  'vitrine.topup.payment.belowWithDetails': 'vitrine.topup.payment.belowWithDetailsCash',
  'vitrine.topup.payment.total': 'vitrine.topup.payment.totalCash',
  'vitrine.topup.payment.estimated': 'vitrine.topup.payment.estimatedCash',
  'vitrine.topup.payment.inSendCurrency': 'vitrine.topup.payment.inSendCurrencyCash',
  'vitrine.topup.payin.title': 'vitrine.topup.payin.titleCash',
  'vitrine.topup.payin.pendingTitle': 'vitrine.topup.payin.pendingTitleCash',
  'vitrine.topup.payin.pendingBody': 'vitrine.topup.payin.pendingBodyCash',
  'vitrine.topup.iSent': 'vitrine.topup.iHandedOver',
  'vitrine.topup.sentAck': 'vitrine.topup.sentAckCash',
  'vitrine.topup.tl.send': 'vitrine.topup.tl.handOver',
  'vitrine.topup.subHint1': 'vitrine.topup.subHint1Cash',
  'vitrine.topup.subHint2': 'vitrine.topup.subHint2Cash',
  'vitrine.topup.statusReported': 'vitrine.topup.statusReportedCash',
  'vitrine.topup.sendCurrencyHint': 'vitrine.topup.sendCurrencyHintCash',
  // Cash walkthrough, follow-up: the form total, the deadline line and the hints still said «оплатите»/«перевод».
  'vitrine.topup.feeTotal': 'vitrine.topup.feeTotalCash',
  'vitrine.topup.deadline.payBy': 'vitrine.topup.deadline.payByCash',
  'vitrine.topup.creditHint': 'vitrine.topup.creditHintCash',
  'vitrine.topup.sendCurrency': 'vitrine.topup.sendCurrencyCash',
  'vitrine.topup.cancelBlocked': 'vitrine.topup.cancelBlockedCash',
  // A concierge payment paid in cash (PayInCard): the same rule — a place and a time, not «реквизиты» and «отправка».
  'vitrine.concierge.payin.title': 'vitrine.concierge.payin.titleCash',
  'vitrine.concierge.payin.details': 'vitrine.concierge.payin.detailsCash',
  'vitrine.concierge.payment.toPay': 'vitrine.concierge.payment.toHandOver',
  'vitrine.concierge.payment.deadline': 'vitrine.concierge.payment.deadlineCash',
  'vitrine.concierge.payment.payInByManager': 'vitrine.concierge.payment.payInByManagerCash',
  'vitrine.concierge.payment.markedSent': 'vitrine.concierge.payment.markedSentCash',
};

/** The key in the request's own words: the cash counterpart for a cash request, else the key itself. */
export function forMethod(key: string, sourceKind: string | null | undefined): string {
  return sourceKind === 'CASH' ? (CASH_WORDING[key] ?? key) : key;
}

/** Every cash counterpart — localeCompleteness.spec.ts checks each exists in all locales. */
export const CASH_WORDING_KEYS: readonly string[] = Object.values(CASH_WORDING);

/** Stage implied by the backend status alone. */
const STAGE_OF: Record<string, number> = {
  SUBMITTED: 0, SCREENING: 0, NEEDS_INFO: 0, APPROVED: 0, EXPORTED: 0,
  IN_PROGRESS: 1, FUNDS_RECEIVED: 2, COMPLETED: 3,
};

const BAD = new Set(['REJECTED', 'CANCELLED', 'EXPIRED']);

export const isTerminalStatus = (s: string): boolean => s === 'COMPLETED' || BAD.has(s);

type DepositPhase = NonNullable<TopUpView['deposit']>['phase'];

/**
 * The deposit phases THIS build knows. The TS union is a promise the server can outgrow: a tab that stays
 * open across a deploy runs old code against a new API (prod 28.09 — SENT_TO_CARD reached a client whose
 * app predated it). Every branch on the phase must therefore have an answer for "none of the above", and
 * that answer is a neutral "in progress" — never the address-expired warning, never a raw key.
 */
const KNOWN_PHASES: ReadonlySet<string> = new Set<DepositPhase>([
  'WAITING_FUNDS', 'FUNDS_SEEN', 'PROCESSING', 'BELOW_MINIMUM', 'AWAITING_CARD', 'SENT_TO_CARD', 'DONE', 'RETURNED',
  'EXPIRED',
]);

export const isKnownPhase = (phase: string | null | undefined): boolean => !!phase && KNOWN_PHASES.has(phase);

/** Neutral phase line for a phase this build does not know or has no string for. */
export const PHASE_IN_PROGRESS_KEY = 'vitrine.topup.deposit.phaseInProgress';

/**
 * The i18n key for the deposit card's phase line. `hasKey` is the component's `te` — the key is built
 * from a server value, so its existence cannot be checked statically (localeCompleteness.spec.ts pins
 * the known phases; this covers the ones it cannot see).
 */
export function depositPhaseKeyOf(phase: string | null | undefined, hasKey: (key: string) => boolean): string {
  if (!isKnownPhase(phase)) return PHASE_IN_PROGRESS_KEY;
  const key = `vitrine.topup.deposit.phase.${phase}`;
  return hasKey(key) ? key : PHASE_IN_PROGRESS_KEY;
}

/**
 * How far along the request is, 0..3 — the SINGLE definition, used by the list row, the pill, the
 * headline and the stepper.
 *
 * <p>Three axes, because three different things can move a request forward and any of them may be
 * ahead of the others: the backend status, the client's own "I sent it" claim, and the on-chain phase.
 * The maximum is taken rather than a priority order — a request never goes backwards on screen.</p>
 */
export function stageOf(r: TopUpView | null | undefined): number {
  if (!r) return 0;
  const byStatus = STAGE_OF[r.status] ?? 0;
  // The client's own claim advances past "you send funds" to "funds received" (awaiting the operator's
  // confirmation) — otherwise pressing the button changed nothing visible. Requisites on screen alone
  // still only imply step 1.
  const byClaim = r.markedSent ? 2 : (r.payInInstructions ? 1 : 0);
  // Rail: the address on screen IS step 1, a detected/processing transfer is step 2, DONE is the end.
  // A phase this build does not know counts as "in progress" (2), not as "send us money" (1): new phases
  // are added further down the pipeline, and step 1 would ask a client who has already paid to pay.
  const byPhase = r.deposit
    ? (r.deposit.phase === 'DONE' ? 3
      : r.deposit.phase === 'FUNDS_SEEN' || r.deposit.phase === 'PROCESSING' || r.deposit.phase === 'BELOW_MINIMUM'
        || r.deposit.phase === 'AWAITING_CARD' || r.deposit.phase === 'SENT_TO_CARD'
        || !isKnownPhase(r.deposit.phase) ? 2
        : 1)
    : 0;
  return Math.max(byStatus, byClaim, byPhase);
}

/**
 * The step the tracker draws as current (cabinet E2E 31.10, Б-6). `stageOf` is the stage REACHED — «Средства получены»
 * at FUNDS_RECEIVED, which the pill reads; but the tracker drew that step as a current circle, not ticked, while the
 * funds were confirmed. Confirmed receipt (FUNDS_RECEIVED) ticks it and moves the current step to the credit. A
 * client's own «Я отправил» alone does not: that receipt is still awaited.
 */
export function trackerIndex(r: TopUpView | null | undefined): number {
  const stage = stageOf(r);
  return r?.status === 'FUNDS_RECEIVED' && stage === 2 ? 3 : stage;
}

/**
 * "We are getting the transfer details" — only while there is still something to send and nowhere to send it.
 *
 * <p>Not once the payment service holds the money: it can confirm receipt without requisites ever having been
 * released (the client paid by details they already had), and the old notice then sat under «Средства получены»
 * telling the client the details were still coming — read as "you still have to pay" (cabinet E2E 10.10).</p>
 */
export function awaitsPayInFor(r: TopUpView | null | undefined): boolean {
  return !!r && !r.payInInstructions && !r.deposit && !isTerminalStatus(r.status)
    // NEEDS_INFO: the manager is waiting for the CLIENT, and the screen says what is needed — not "details are coming".
    // markedSent: the server hides released requisites again while a request is held; a client who already paid
    // must not read that as "you still have to pay" (review 10.10, M3).
    && r.status !== 'NEEDS_INFO' && !r.markedSent
    && (STAGE_OF[r.status] ?? 0) < 2;
}

/**
 * QA 19.10: a top-up funded through the payment service whose money is in («Средства получены») waits for the card
 * credit, which only the card provider's own event confirms. The badge stays the real status; this says what is
 * awaited, so the client does not read «Средства получены» as the end. Off the crypto rail (it has its own phase line).
 */
export function awaitsCardCreditFor(r: TopUpView | null | undefined): boolean {
  return !!r && !r.deposit && r.status === 'FUNDS_RECEIVED' && r.requestedCreditAmount != null;
}

/**
 * The i18n key for the status badge — the same one for the list row and the detail card.
 *
 * <p>Deliberately NOT the phase line's key. The phase line already renders the phase in full sentences
 * ("Мы видим ваш перевод — ждём подтверждений сети"); repeating it in an 11.5 px badge would be a
 * verbatim duplicate in most states and would not fit in the rest. The badge answers the shorter
 * question — where are we — and for the rail it answers it from the phase rather than from the fiat
 * timeline's wording.</p>
 */
/** A funded rail request whose money went entirely towards the card price — nothing was credited to the card. */
function paidOnlyTheCardPrice(r: TopUpView): boolean {
  return !!r.deposit && (r.status === 'COMPLETED' || r.status === 'FUNDS_RECEIVED')
    && Number(r.creditedAmount ?? 0) === 0 && Number(r.breakdown?.cardPrice ?? 0) > 0;
}

export function pillKeyOf(r: TopUpView | null | undefined): string {
  if (!r) return STAGES[0];
  if (BAD.has(r.status)) {
    return r.status === 'REJECTED' ? 'vitrine.topup.status.rejected' : 'vitrine.topup.statusCancelled';
  }
  // All of a rail payment went to the card price (50 sent for a 50 card): nothing reached the card, so "Карта
  // пополнена" read as money gone missing next to a 0 balance (E2E 30.09). Say what it paid for.
  if (paidOnlyTheCardPrice(r)) {
    return r.status === 'COMPLETED' ? 'vitrine.topup.railPill.cardPaid' : 'vitrine.topup.railPill.cardPartlyPaid';
  }
  if (r.status === 'COMPLETED') return 'vitrine.topup.tl.credited';
  if (r.deposit) {
    // The rail has its own short vocabulary: the fiat timeline's labels were written for a flow with an
    // operator, a quote and pay-in requisites, and reading "Вы отправляете средства" over an on-chain
    // phase is what made the screen contradict itself.
    switch (r.deposit.phase) {
      case 'WAITING_FUNDS': return 'vitrine.topup.railPill.waiting';
      case 'FUNDS_SEEN': return 'vitrine.topup.railPill.seen';
      case 'PROCESSING': return 'vitrine.topup.railPill.processing';
      // Nothing is being processed: the sum is under the minimum and waits for more on the same address.
      case 'BELOW_MINIMUM': return 'vitrine.topup.railPill.belowMinimum';
      case 'AWAITING_CARD': return 'vitrine.topup.railPill.awaitingCard';
      // Our transfer reached the card wallet; the provider has not credited the card yet. Not "Карта пополнена" —
      // that is the provider's word, and the prod 28.09 complaint was hearing it before the provider said it.
      case 'SENT_TO_CARD': return 'vitrine.topup.railPill.sentToCard';
      case 'RETURNED': return 'vitrine.topup.railPill.returned';
      case 'EXPIRED': return 'vitrine.topup.railPill.expired';
      // Unknown to this build: neutral. "Перевод получен" (processing) would assert something it cannot know.
      default: return 'vitrine.topup.railPill.inProgress';
    }
  }
  // The client declared the transfer but nobody has confirmed receipt — the stage is optimistic, the
  // badge must not be.
  if (r.markedSent && (STAGE_OF[r.status] ?? 0) < 2) return forMethod('vitrine.topup.statusReported', r.sourceKind);
  return forMethod(STAGES[stageOf(r)], r.sourceKind);
}

/**
 * Badge colour. For a rail request it follows the PHASE, not the status: a request whose address has
 * expired keeps `status = APPROVED`, so a status-driven colour painted "срок истёк" in the positive
 * gold of a request that is going fine.
 */
export function pillClassOf(r: TopUpView | null | undefined): string {
  if (!r) return 'tu__pill--go';
  if (BAD.has(r.status)) return 'tu__pill--bad';
  if (r.status === 'COMPLETED') return 'tu__pill--done';
  if (r.deposit) {
    if (r.deposit.phase === 'DONE') return 'tu__pill--done';
    if (r.deposit.phase === 'RETURNED' || r.deposit.phase === 'EXPIRED') return 'tu__pill--bad';
  }
  return 'tu__pill--go';
}

/**
 * What the row shows where a fiat request shows its declared amount.
 *
 * <p>The rail has no declared amount (REQ-S1) — the client sends what they want — so the row used to
 * render an empty string next to the currency and read simply "USDT". Null here means "there is no
 * number yet"; the caller shows the status instead of a bare currency code.</p>
 */
export function rowAmountOf(r: TopUpView): { amount: string; currency: string } | null {
  if (r.deposit) {
    // What the client SENT — the number they know from their own wallet. The credit (97 of 100) read as
    // money lost when it headed the request (owner, 26.09); it is explained in the breakdown instead.
    if (r.breakdown && Number(r.breakdown.received) > 0) {
      return { amount: String(r.breakdown.received), currency: r.deposit.asset ?? r.amountCurrency };
    }
    const credited = Number(r.creditedAmount ?? 0);
    if (credited > 0) {
      return { amount: String(r.creditedAmount), currency: r.deposit.asset ?? r.amountCurrency };
    }
    return null;
  }
  if (r.amount == null) {
    // PayFlow spec 07 В1: a bank/cash request funded through Transfer Hub names what goes ON THE CARD, in the card's
    // currency — the client's own number. The amount in the send currency is the provider's quote, not ours to show.
    if (r.requestedCreditAmount != null && r.requestedCreditCurrency) {
      return { amount: String(r.requestedCreditAmount), currency: r.requestedCreditCurrency };
    }
    return null;
  }
  return { amount: r.amount, currency: r.amountCurrency };
}

/**
 * Does the screen still ASK for money? Phase AND status, because either alone lets the worst case
 * through: a cancelled request used to report `WAITING_FUNDS`, and a live request keeps `APPROVED`
 * while its address dies.
 */
export function showDepositCallFor(r: TopUpView | null | undefined): boolean {
  if (!r?.deposit || isTerminalStatus(r.status)) return false;
  const p = r.deposit.phase;
  // PROCESSING and AWAITING_CARD still show it: both mean the address is alive. PROCESSING is reached
  // when ONE arrival clears PENDING, and AWAITING_CARD means the money is here but has nowhere to sweep
  // to yet — in either case a client who sent part of the amount may legitimately top the same address
  // up, which the screen explicitly promises ("вы вернётесь к нему же").
  // SENT_TO_CARD too, for the same reason: the money has left for the card, but the address is the client's and
  // alive — without it the card would read "the address has expired" over a request that is going fine.
  // BELOW_MINIMUM above all: the client has to send more to this very address.
  return p === 'WAITING_FUNDS' || p === 'FUNDS_SEEN' || p === 'PROCESSING' || p === 'BELOW_MINIMUM'
    || p === 'AWAITING_CARD' || p === 'SENT_TO_CARD';
}

/**
 * "This address has expired — create a new request": only for phases that really mean the address is
 * gone. It used to be the plain `else` of {@link showDepositCallFor}, so a phase the build did not know
 * (SENT_TO_CARD on an app opened before the 28.09 deploy) told a client whose money was already on its
 * way to the card that they had to start over.
 */
export function showAddressClosedFor(r: TopUpView | null | undefined): boolean {
  if (!r?.deposit || isTerminalStatus(r.status)) return false;
  return isKnownPhase(r.deposit.phase) && !showDepositCallFor(r);
}

/** "Credited within an hour" — only while a credit is actually still expected. */
export function showCreditEtaFor(r: TopUpView | null | undefined): boolean {
  if (!r?.deposit || isTerminalStatus(r.status)) return false;
  const p = r.deposit.phase;
  // Not in AWAITING_CARD: that wait is bounded by a card being issued, not by network confirmation, and
  // the operator's own alert threshold for it is 24 hours.
  return p === 'WAITING_FUNDS' || p === 'FUNDS_SEEN' || p === 'PROCESSING';
}
